import { useEffect } from "react";
import { Link } from "react-router-dom";
import type {
  CtaLink,
  Publication,
  ResearchPageContent,
} from "../content/types";

function Cta({ cta, variant }: { cta: CtaLink; variant: "primary" | "quiet" }) {
  const className = variant === "primary" ? "btn btn--primary" : "btn btn--quiet";
  if (cta.external || cta.href.startsWith("#")) {
    return (
      <a className={className} href={cta.href}>
        {cta.label}
      </a>
    );
  }
  return (
    <Link className={className} to={cta.href}>
      {cta.label}
    </Link>
  );
}

/**
 * One published work.
 *
 * The identifier is rendered, not hidden behind the link text: a DOI or an ISBN
 * is the thing a reader cites, and showing it is what distinguishes a real
 * bibliography from a list of buttons. Links carry `rel="noreferrer"` because
 * they leave for publishers we do not control.
 */
function PublicationEntry({ pub }: { pub: Publication }) {
  const identifier = pub.doi ?? (pub.isbn ? `ISBN ${pub.isbn}` : undefined);

  return (
    <li className="pub">
      <a
        className="pub__title"
        href={pub.href}
        target="_blank"
        rel="noreferrer"
      >
        {pub.title}
      </a>
      <p className="pub__summary">{pub.summary}</p>
      <p className="pub__meta">
        <span>{pub.venue}</span>
        {identifier && <code className="pub__id">{identifier}</code>}
        {pub.archive && (
          <a href={pub.archive.href} target="_blank" rel="noreferrer">
            {pub.archive.label} →
          </a>
        )}
      </p>
    </li>
  );
}

/**
 * The research page.
 *
 * Papers are grouped by the argument they advance rather than listed by date.
 * A reverse-chronological list makes nine preprints look like nine unrelated
 * PDFs; grouped, they read as four lines of work, which is what they are.
 */
const ORCID = "https://orcid.org/0009-0002-2878-732X";

/**
 * ScholarlyArticle structured data for every DOI-bearing work on the page, so
 * search and AI crawlers can tie each paper to its identifier and author. Rendered
 * in the prerendered HTML; books are excluded because they carry ISBNs, not DOIs.
 */
function researchJsonLd(content: ResearchPageContent): string {
  const articles = content.lines.flatMap((line) =>
    line.publications
      .filter((pub) => pub.doi)
      .map((pub) => ({
        "@type": "ScholarlyArticle",
        headline: pub.title,
        abstract: pub.summary,
        identifier: { "@type": "PropertyValue", propertyID: "DOI", value: pub.doi },
        url: pub.href,
        sameAs: pub.href,
        about: line.title,
        author: { "@type": "Person", name: "Andre Byrd", sameAs: ORCID },
        publisher: { "@type": "Organization", name: "Odingard Security" },
        creativeWorkStatus: /working paper/i.test(pub.venue) ? "Working paper" : "Preprint",
        datePublished: pub.venue.match(/\d{4}/)?.[0],
      })),
  );
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Odingard Research",
    url: "https://odingard.com/research",
    hasPart: articles,
  }).replace(/</g, "\\u003c");
}

export function ResearchPage({ content }: { content: ResearchPageContent }) {
  useEffect(() => {
    document.title = "Research — Odingard";
  }, []);

  return (
    <main className="page pdp research" id="main">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: researchJsonLd(content) }}
      />
      <section className="pdp__hero">
        <p className="page__eyebrow">{content.eyebrow}</p>
        <h1>{content.headline}</h1>
        <p className="page__lede">{content.lede}</p>
        <div className="pdp__ctas">
          {content.ctas.map((cta, i) => (
            <Cta key={cta.href} cta={cta} variant={i === 0 ? "primary" : "quiet"} />
          ))}
        </div>
      </section>

      {content.lines.map((line) => (
        <section className="pdp__section" key={line.title}>
          <h2>{line.title}</h2>
          <p className="pdp__body">{line.body}</p>
          <ul className="pub__list">
            {line.publications.map((pub) => (
              <PublicationEntry key={pub.href} pub={pub} />
            ))}
          </ul>
        </section>
      ))}

      <section className="pdp__section" id="books">
        <h2>{content.books.heading}</h2>
        <p className="pdp__body">{content.books.lede}</p>
        <ul className="pub__list">
          {content.books.items.map((book) => (
            <PublicationEntry key={book.href} pub={book} />
          ))}
        </ul>
      </section>

      <section className="pdp__section pdp__section--evidence" id="artifacts">
        <h2>{content.artifacts.heading}</h2>
        <p className="pdp__body">{content.artifacts.lede}</p>
        <div className="pdp__features">
          {content.artifacts.items.map((item) => (
            <article key={item.href}>
              <h3>
                <a href={item.href} target="_blank" rel="noreferrer">
                  {item.name}
                </a>
              </h3>
              <p>{item.body}</p>
              <p className="pub__meta">
                <code className="pub__id">{item.license}</code>
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="pdp__close" id="close">
        <h2>{content.close.heading}</h2>
        <p className="pdp__body">{content.close.body}</p>
        <div className="pdp__ctas">
          {content.close.ctas.map((cta, i) => (
            <Cta key={cta.href} cta={cta} variant={i === 0 ? "primary" : "quiet"} />
          ))}
        </div>
      </section>
    </main>
  );
}
